/**
 * Hook for managing tab-specific URL params
 * Saves and restores params for each tab separately
 */

import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { urlParams } from '../utils';

interface UseTabParamsOptions {
  defaultTab?: string;
}

export function useTabParams(options: UseTabParamsOptions = {}) {
  const { defaultTab = 'by-client' } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Store params for each tab separately (excluding 'tab' param) - using ref to avoid re-renders
  const tabParamsRef = useRef<Record<string, string>>({});
  const isTabChangingRef = useRef(false);
  const isInitializedRef = useRef(false);

  // Get tab from query parameter - memoized to avoid unnecessary recalculations
  const tabParam = useMemo(() => {
    return searchParams.get('tab') || defaultTab;
  }, [searchParams, defaultTab]);

  // Set default tab if not present in query params (only once on mount)
  useEffect(() => {
    if (!isInitializedRef.current && !searchParams.get('tab')) {
      isInitializedRef.current = true;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', defaultTab);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, defaultTab]);

  // Save current tab's params whenever searchParams change (but not during tab switch)
  useEffect(() => {
    // Skip saving during tab switch or if no params exist
    if (isTabChangingRef.current) {
      isTabChangingRef.current = false;
      return;
    }

    const paramsString = urlParams.getParamsWithoutTab(searchParams);

    // Only save if params exist (avoid clearing saved params with empty string)
    if (paramsString) {
      tabParamsRef.current[tabParam] = paramsString;
    }
  }, [searchParams, tabParam]);

  /**
   * Handle tab change with params save/restore
   */
  const handleTabChange = useCallback(
    (tabId: string) => {
      const targetTab = tabId || defaultTab;

      // Don't do anything if already on this tab
      if (targetTab === tabParam) {
        return;
      }

      // Save current tab's params before switching
      const currentParamsString = urlParams.getParamsWithoutTab(searchParams);
      if (currentParamsString) {
        tabParamsRef.current[tabParam] = currentParamsString;
      }

      // Mark as tab changing to prevent saving in the next effect
      isTabChangingRef.current = true;

      // Build new URL with target tab and restored params
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('tab', targetTab);

      // Restore saved params for the target tab if they exist
      const savedParamsString = tabParamsRef.current[targetTab];
      if (savedParamsString) {
        urlParams.restoreParamsInto(newSearchParams, savedParamsString);
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [tabParam, defaultTab, searchParams, setSearchParams]
  );

  return {
    tabParam,
    handleTabChange,
  };
}

